/**
 * TexturePromotion — grow a resident compressed texture's mip chain on the
 * GPU without re-uploading the levels it already has (settlement LOD0
 * research L2, 2026-09-03).
 *
 * A streamed "skin" carries only the top levels of an image (1024 px, or
 * 2048 + 1024) as a small KTX2; the levels below are already resident in
 * the 512 px texture every LOD level shares. Promotion allocates a new
 * GPUTexture with the full chain, copies the skin's levels and the resident
 * texture's levels into it with `copyTextureToTexture` (block-compressed
 * copies are allowed outside WebGPU Compatibility Mode; sizes are rounded to
 * the 4-texel block so the sub-4 tail levels validate against their physical
 * size), then installs the merged texture as the three.js texture's backend
 * object — no CPU bytes cross for the lower levels, no version bump, so the
 * renderer never re-uploads; only the sampled-texture bindings refresh
 * (the Textures data generation is bumped and the cached views dropped).
 * `demoteCompressedTexture` swaps the retained original back.
 *
 * WebGL2 and Compatibility Mode report `unsupported` — callers keep a
 * full-chain fallback (this module never uploads from CPU arrays).
 */
import type * as THREE from 'three/webgpu';

/** One GPU-side level copy in a promotion plan. */
export interface LevelCopy { from: 'top' | 'base'; srcLevel: number; dstLevel: number; width: number; height: number }

/** The copies that merge `top` (levels above the base) with `base` into one chain. */
export interface PromotionPlan { width: number; height: number; mipLevelCount: number; copies: LevelCopy[] }

/** Round a level extent up to the compressed block (4 texels) — WebGPU validates compressed copies against the PHYSICAL size. */
export function blockAligned(extent: number, block = 4): number {
  return Math.max(block, Math.ceil(Math.max(1, extent) / block) * block);
}

/**
 * Plan a promotion. `top` must be exactly the levels above `base`: its
 * smallest level is one step above the base's largest. Returns null when
 * the chains do not line up (the caller then falls back).
 */
export function planPromotion(base: { width: number; height: number; levels: number }, top: { width: number; height: number; levels: number }): PromotionPlan | null {
  if (top.levels < 1 || base.levels < 1) return null;
  const topLastW = Math.max(1, top.width >> (top.levels - 1)), topLastH = Math.max(1, top.height >> (top.levels - 1));
  if (topLastW !== base.width * 2 || topLastH !== base.height * 2) return null;
  const copies: LevelCopy[] = [];
  for (let i = 0; i < top.levels; i++) copies.push({ from: 'top', srcLevel: i, dstLevel: i, width: blockAligned(top.width >> i), height: blockAligned(top.height >> i) });
  for (let j = 0; j < base.levels; j++) copies.push({ from: 'base', srcLevel: j, dstLevel: top.levels + j, width: blockAligned(base.width >> j), height: blockAligned(base.height >> j) });
  return { width: top.width, height: top.height, mipLevelCount: top.levels + base.levels, copies };
}

/** Minimal views of three's WebGPU internals this module touches. */
interface GPUTextureLike { width: number; height: number; mipLevelCount: number; destroy(): void }
interface TextureDataLike { texture?: GPUTextureLike; format?: string; initialized?: boolean; version?: number; promotedFrom?: GPUTextureLike; promotedSize?: { width: number; height: number; levels: number }; [k: string]: unknown }
interface BackendLike {
  isWebGPUBackend?: boolean;
  device?: {
    createTexture(desc: Record<string, unknown>): GPUTextureLike;
    createCommandEncoder(desc?: Record<string, unknown>): { copyTextureToTexture(src: Record<string, unknown>, dst: Record<string, unknown>, size: Record<string, unknown>): void; finish(): unknown };
    queue: { submit(cmds: unknown[]): void };
  };
  get(texture: THREE.Texture): TextureDataLike;
  parameters?: { compatibilityMode?: boolean };
  compatibilityMode?: boolean;
}
interface RendererLike { backend?: BackendLike; _textures?: { get(texture: THREE.Texture): { generation?: number } }; initTexture?(t: THREE.Texture): void }

/** Why a promotion could not run (the caller's fallback decision). */
export type PromotionResult = 'promoted' | 'unsupported' | 'not-resident' | 'mismatch' | 'already';

/** Diagnostics counters (probes). */
export const texturePromotionStats = { promoted: 0, demoted: 0, unsupported: 0, mismatch: 0, copies: 0 };

function usage(): number {
  const g = (globalThis as unknown as { GPUTextureUsage?: { TEXTURE_BINDING: number; COPY_DST: number; COPY_SRC: number } }).GPUTextureUsage;
  return g ? (g.TEXTURE_BINDING | g.COPY_DST | g.COPY_SRC) : 0x1 | 0x2 | 0x4;
}

/** Drop the bind-group texture views cached on the backend data and bump the bindings generation so every user rebinds. */
function refreshBindings(renderer: RendererLike, texture: THREE.Texture, data: TextureDataLike): void {
  for (const k of Object.keys(data)) if (k.startsWith('view-')) delete data[k];
  const td = renderer._textures?.get(texture);
  if (td) td.generation = (typeof td.generation === 'number' ? td.generation : 0) + 1;
}

/**
 * Promote `base` (resident, e.g. 512 px) with `top` (the levels above it, an
 * unresident or resident compressed texture). On success `base` samples the
 * full chain everywhere it is bound; `top` is disposed. The original GPU
 * texture is retained on the backend data for `demoteCompressedTexture`.
 */
export function promoteCompressedTexture(rendererNative: unknown, base: THREE.Texture, top: THREE.Texture): PromotionResult {
  const renderer = rendererNative as RendererLike;
  const backend = renderer.backend;
  if (!backend?.isWebGPUBackend || !backend.device) { texturePromotionStats.unsupported++; return 'unsupported'; }
  if (backend.parameters?.compatibilityMode || backend.compatibilityMode) { texturePromotionStats.unsupported++; return 'unsupported'; }
  const baseData = backend.get(base);
  if (baseData.promotedFrom) return 'already';
  // Both must be GPU-resident (the skin uploads from its CPU levels here; the base already is).
  try { renderer.initTexture?.(top); if (!baseData.texture) renderer.initTexture?.(base); } catch { /* fall through to the residency check */ }
  const topData = backend.get(top);
  if (!baseData.texture || !topData.texture) { return 'not-resident'; }
  if (baseData.format !== topData.format) { texturePromotionStats.mismatch++; return 'mismatch'; }
  const b = baseData.texture, t = topData.texture;
  const plan = planPromotion({ width: b.width, height: b.height, levels: b.mipLevelCount }, { width: t.width, height: t.height, levels: t.mipLevelCount });
  if (!plan) { texturePromotionStats.mismatch++; return 'mismatch'; }
  const merged = backend.device.createTexture({
    label: `${(base as { name?: string }).name || 'texture'}-promoted`,
    size: { width: plan.width, height: plan.height, depthOrArrayLayers: 1 },
    mipLevelCount: plan.mipLevelCount, sampleCount: 1, dimension: '2d', format: baseData.format, usage: usage(),
  });
  const encoder = backend.device.createCommandEncoder({ label: 'texturePromotion' });
  for (const c of plan.copies) {
    encoder.copyTextureToTexture({ texture: c.from === 'top' ? t : b, mipLevel: c.srcLevel }, { texture: merged, mipLevel: c.dstLevel }, { width: c.width, height: c.height, depthOrArrayLayers: 1 });
  }
  backend.device.queue.submit([encoder.finish()]);
  texturePromotionStats.copies += plan.copies.length;
  baseData.promotedFrom = b;
  baseData.promotedSize = { width: b.width, height: b.height, levels: b.mipLevelCount };
  baseData.texture = merged;
  const img = (base as { image?: { width?: number; height?: number } }).image;
  if (img && typeof img === 'object') { img.width = plan.width; img.height = plan.height; }
  refreshBindings(renderer, base, baseData);
  try { top.dispose(); } catch { /* the skin's GPU copy is freed by the backend's dispose listener */ }
  texturePromotionStats.promoted++;
  return 'promoted';
}

/** Swap a promoted texture back to its retained original chain and free the merged texture. */
export function demoteCompressedTexture(rendererNative: unknown, base: THREE.Texture): boolean {
  const renderer = rendererNative as RendererLike;
  const backend = renderer.backend;
  if (!backend?.isWebGPUBackend) return false;
  const data = backend.get(base);
  const orig = data.promotedFrom;
  if (!orig) return false;
  const merged = data.texture;
  data.texture = orig;
  delete data.promotedFrom;
  const size = data.promotedSize; delete data.promotedSize;
  const img = (base as { image?: { width?: number; height?: number } }).image;
  if (img && typeof img === 'object' && size) { img.width = size.width; img.height = size.height; }
  refreshBindings(renderer, base, data);
  try { merged?.destroy(); } catch { /* already gone */ }
  texturePromotionStats.demoted++;
  return true;
}

/** True when `base` currently samples a promoted chain. */
export function isPromoted(rendererNative: unknown, base: THREE.Texture): boolean {
  const backend = (rendererNative as RendererLike).backend;
  return !!backend?.isWebGPUBackend && !!backend.get(base).promotedFrom;
}
